import { jwtDecode } from 'jwt-decode';
import type { NextApiRequest } from 'next';
import type { JWT } from 'next-auth/jwt';
import type { AuthorizationEndpointHandler } from 'next-auth/providers';
import { getTenantName } from './next.helpers';
import {
  generateSinglePlayerToken,
  isSinglePlayerMode
} from './single-player-auth.helper';

export const REFRESH_TOKEN_EXPIRED = 'RefreshTokenExpired';

/**
 * Dynamically build Keycloak Issuer URL based on the current request.
 * Use environment variables if defined, otherwise, attempt to infer from
 * request host.
 *
 * Localhost should always have the environment variable defined
 */
export const keycloakIssuer = (req: NextApiRequest): string => {
  // use env if defined (typically local and Vercel deployments)
  if (process.env.KEYCLOAK_ISSUER) {
    return process.env.KEYCLOAK_ISSUER;
  }
  // otherwise, infer from request host
  const tenant = getTenantName(req);
  if (tenant) {
    return `https://login.${tenant}.moderne.io/auth/realms/${tenant}`;
  } else {
    throw new Error('No KEYCLOAK_ISSUER or tenant found');
  }
};

/**
 * Dynamically get the Keycloak client ID based on the current request.
 * Use environment variables if defined, otherwise, attempt to infer from
 * request host.
 *
 * Localhost should always have the environment variable defined
 */
export const keycloakClientId = (req: NextApiRequest): string => {
  // use env if defined (typically local and Vercel deployments)
  if (process.env.KEYCLOAK_CLIENT_ID) {
    return process.env.KEYCLOAK_CLIENT_ID;
  }
  // otherwise, infer from request host
  const tenant = getTenantName(req);
  if (tenant) {
    return `${tenant}-moderne`;
  } else {
    throw new Error('No KEYCLOAK_CLIENT_ID or tenant found');
  }
};

// In-memory map to track ongoing refresh requests to prevent race conditions
const ongoingRefreshes = new Map<string, Promise<JWT>>();

/**
 * Core token refresh logic that makes the actual HTTP request to Keycloak.
 * Separated from refreshAccessToken to enable deduplication.
 */
async function performTokenRefresh(
  req: NextApiRequest,
  token: JWT
): Promise<JWT> {
  const url = `${keycloakIssuer(req)}/protocol/openid-connect/token`;

  const postBody = new URLSearchParams({
    client_id: keycloakClientId(req),
    client_secret: process.env.KEYCLOAK_CLIENT_SECRET,
    grant_type: 'refresh_token',
    refresh_token: token.refreshToken
  });

  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded'
    },
    method: 'POST',
    body: postBody
  });

  const refreshedTokens = await response.json();

  if (!response.ok) {
    return {
      ...token,
      error: REFRESH_TOKEN_EXPIRED
    };
  }

  const newToken = {
    ...token,
    accessToken: refreshedTokens.access_token,
    accessTokenExpires: Date.now() + refreshedTokens.expires_in * 1000,
    refreshToken: refreshedTokens.refresh_token ?? token.refreshToken // Fall back to old refresh token
  };
  debugToken(newToken, 'refreshed');
  return newToken;
}

/**
 * Takes a token, and returns a new token with updated
 * `accessToken` and `accessTokenExpires`. If an error occurs,
 * returns the old token and an error property.
 *
 * This function implements deduplication to prevent race conditions
 * when multiple tabs simultaneously attempt to refresh the same token.
 */
export async function refreshAccessToken(
  req: NextApiRequest,
  token: JWT
): Promise<JWT> {
  // In single-player mode, just regenerate the token (no Keycloak call)
  if (isSinglePlayerMode()) {
    return {
      ...token,
      accessToken: generateSinglePlayerToken(),
      accessTokenExpires: Date.now() + 86400000 // 24 hours
    };
  }

  const refreshKey = token.refreshToken;

  // If a refresh for this token is already in progress, return the existing promise
  if (ongoingRefreshes.has(refreshKey)) {
    return ongoingRefreshes.get(refreshKey)!;
  }

  // Start a new refresh operation
  const refreshPromise = performTokenRefresh(req, token);
  ongoingRefreshes.set(refreshKey, refreshPromise);

  // Clean up the promise from the map when it completes (success or failure)
  refreshPromise.finally(() => {
    ongoingRefreshes.delete(refreshKey);
  });

  return refreshPromise;
}

export const debugToken = (jwtToken: Partial<JWT>, _origin: string) => {
  if (process.env.NODE_ENV !== 'development') {
    return;
  }
  const _decodedToken = jwtDecode<{ iat: number; exp: number }>(
    jwtToken.accessToken as string
  );
  const _decodedRefreshToken = jwtDecode<{ iat: number; exp: number }>(
    jwtToken.refreshToken as string
  );
};

export const getProviderAuthorizationOptions = (req: NextApiRequest) => {
  let authorization: AuthorizationEndpointHandler = undefined;
  if (req.cookies['moderne-no-hint']) {
    authorization = {
      params: {
        kc_idp_hint: ''
      }
    };
  }
  return authorization;
};

type KeycloakProfileNameFields = {
  name?: string;
  given_name?: string;
  family_name?: string;
  preferred_username?: string;
  email?: string;
};

/**
 * Derives a display name from a Keycloak/OIDC profile. Trims each candidate
 * so a whitespace-only IdP value can't win over a real fallback, and always
 * returns a string (never undefined) so callers can't crash on a missing name.
 */
export const resolveFullName = (profile: KeycloakProfileNameFields): string => {
  const name = profile.name?.trim();
  if (name) {
    return name;
  }

  const givenFamily = [profile.given_name, profile.family_name]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ');
  if (givenFamily) {
    return givenFamily;
  }

  const preferredUsername = profile.preferred_username?.trim();
  if (preferredUsername) {
    return preferredUsername;
  }

  return profile.email?.trim() ?? '';
};

export const determineRedirectUrl = (req: NextApiRequest): string => {
  if (process.env.NEXTAUTH_URL) {
    return process.env.NEXTAUTH_URL;
  }

  if (process.env.VERCEL_URL) {
    return `https://${process.env.VERCEL_URL}`;
  }

  if (req?.headers?.referer) {
    try {
      const url = new URL(req?.headers?.referer);
      return url.origin;
    } catch {
      return undefined;
    }
  }
  return undefined;
};
